/**
 * useQueueSounds - React hook for managing queue sound notifications
 * QUEUE-007: Sound notifications for queue events
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { onSnapshot, doc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { QueueTicket } from '../types';
import { soundManager, SoundType, SoundConfig } from '../services/QueueSoundManager';
import { useAuth } from '../contexts/AuthContext';

export interface QueueSoundEvents {
  onPositionChange?: (oldPosition: number, newPosition: number) => void;
  onTurnApproaching?: (turnsLeft: number) => void;
  onTurnReady?: () => void;
  onTicketCalled?: () => void;
  onTicketCompleted?: () => void;
  onTicketCancelled?: () => void;
}

export interface UseQueueSoundsReturn {
  // Sound control methods
  playSound: (type: SoundType) => Promise<void>;
  testSound: (type: SoundType) => Promise<void>;

  // Configuration
  soundConfig: SoundConfig;
  setSoundEnabled: (enabled: boolean) => void;
  setSoundVolume: (volume: number) => void;
  setSoundTypeEnabled: (type: SoundType, enabled: boolean) => void;
  setVibrationEnabled: (enabled: boolean) => void;

  // Status
  isInitialized: boolean;
  isMonitoring: boolean;
  currentPosition: number | null;
}

export interface UseQueueSoundsOptions {
  ticketId?: string;
  enableMonitoring?: boolean;
  events?: QueueSoundEvents;
  alertThreshold?: number; // Number of positions before user's turn to start alerts
}

const DEFAULT_ALERT_THRESHOLD = 3;

export function useQueueSounds(options: UseQueueSoundsOptions = {}): UseQueueSoundsReturn {
  const {
    ticketId,
    enableMonitoring = true,
    events = {},
    alertThreshold = DEFAULT_ALERT_THRESHOLD,
  } = options;

  const { user: currentUser } = useAuth();
  const [isInitialized, setIsInitialized] = useState(false);
  const [isMonitoring, setIsMonitoring] = useState(false);
  const [soundConfig, setSoundConfig] = useState<SoundConfig>(soundManager.getConfig());
  const [currentPosition, setCurrentPosition] = useState<number | null>(null);

  // Refs to track previous state
  const previousTicketRef = useRef<QueueTicket | null>(null);
  const previousPositionRef = useRef<number | null>(null);
  const hasPlayedTurnAlertRef = useRef(false);
  const lastSoundTimeRef = useRef<{ [key: string]: number }>({});

  // Initialize sound manager
  useEffect(() => {
    const initializeSounds = async () => {
      try {
        await soundManager.initialize();
        setIsInitialized(true);
      } catch (error) {
        console.error('Failed to initialize queue sounds:', error);
      }
    };

    initializeSounds();

    return () => {
      // Cleanup if needed
    };
  }, []);

  // Play sound with throttling to prevent spam
  const playSound = useCallback(async (type: SoundType) => {
    const now = Date.now();
    const lastTime = lastSoundTimeRef.current[type] || 0;
    const minInterval = 1000; // Minimum 1 second between same sound

    if (now - lastTime < minInterval) {
      return; // Throttle sound
    }

    lastSoundTimeRef.current[type] = now;
    await soundManager.playSound(type);
  }, []);

  // Test a specific sound
  const testSound = useCallback(async (type: SoundType) => {
    await soundManager.testSound(type);
  }, []);

  // Configuration methods
  const setSoundEnabled = useCallback((enabled: boolean) => {
    soundManager.setEnabled(enabled);
    setSoundConfig(soundManager.getConfig());
  }, []);

  const setSoundVolume = useCallback((volume: number) => {
    soundManager.setVolume(volume);
    setSoundConfig(soundManager.getConfig());
  }, []);

  const setSoundTypeEnabled = useCallback((type: SoundType, enabled: boolean) => {
    soundManager.setSoundTypeEnabled(type, enabled);
    setSoundConfig(soundManager.getConfig());
  }, []);

  const setVibrationEnabled = useCallback((enabled: boolean) => {
    soundManager.updateConfig({ vibration: enabled });
    setSoundConfig(soundManager.getConfig());
  }, []);

  // Monitor ticket changes
  useEffect(() => {
    if (!ticketId || !enableMonitoring || !isInitialized) {
      setIsMonitoring(false);
      return;
    }

    let unsubscribe: (() => void) | null = null;

    const setupMonitoring = async () => {
      try {
        // Subscribe to ticket changes
        unsubscribe = onSnapshot(
          doc(db, 'queues', ticketId),
          (snapshot) => {
            if (!snapshot.exists()) {
              setIsMonitoring(false);
              return;
            }

            const ticketData = { id: snapshot.id, ...snapshot.data() } as unknown as QueueTicket;
            const previousTicket = previousTicketRef.current;

            // Calculate position in queue
            const position = ticketData.position || 0;
            setCurrentPosition(position);

            // Check for status changes
            if (previousTicket) {
              // Position change
              if (previousTicket.position !== ticketData.position) {
                const oldPos = previousTicket.position || 0;
                const newPos = ticketData.position || 0;

                // Position improved (moved forward in queue)
                if (newPos < oldPos && newPos > 0) {
                  playSound('notification');

                  // Check if turn is approaching
                  if (newPos <= alertThreshold && newPos > 1) {
                    if (!hasPlayedTurnAlertRef.current) {
                      playSound('turn-alert');
                      hasPlayedTurnAlertRef.current = true;
                      events.onTurnApproaching?.(newPos);
                    }
                  }

                  // User's turn
                  if (newPos === 1) {
                    playSound('turn-alert');
                    events.onTurnReady?.();
                  }

                  events.onPositionChange?.(oldPos, newPos);
                }
              }

              // Status changes
              if (previousTicket.status !== ticketData.status) {
                switch (ticketData.status) {
                  case 'notified':
                    // User's ticket is being called
                    if (ticketData.userId === currentUser?.id) {
                      playSound('turn-alert');
                      events.onTicketCalled?.();
                    }
                    break;

                  case 'in_service':
                    // Service started
                    if (ticketData.userId === currentUser?.id) {
                      playSound('success');
                    }
                    break;

                  case 'completed':
                    // Service completed
                    if (ticketData.userId === currentUser?.id) {
                      playSound('success');
                      events.onTicketCompleted?.();
                    }
                    break;

                  case 'cancelled':
                    // Ticket cancelled
                    if (ticketData.userId === currentUser?.id) {
                      playSound('warning');
                      events.onTicketCancelled?.();
                    }
                    break;

                  case 'expired':
                    // User didn't show up
                    if (ticketData.userId === currentUser?.id) {
                      playSound('warning');
                    }
                    break;
                }
              }
            }

            // Update refs for next comparison
            previousTicketRef.current = ticketData;
            previousPositionRef.current = position;
          },
          (error) => {
            console.error('Error monitoring ticket:', error);
            setIsMonitoring(false);
          }
        );

        setIsMonitoring(true);
      } catch (error) {
        console.error('Failed to setup ticket monitoring:', error);
        setIsMonitoring(false);
      }
    };

    setupMonitoring();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
      setIsMonitoring(false);
      previousTicketRef.current = null;
      previousPositionRef.current = null;
      hasPlayedTurnAlertRef.current = false;
    };
  }, [
    ticketId,
    enableMonitoring,
    isInitialized,
    currentUser?.id,
    playSound,
    events,
    alertThreshold,
  ]);

  // Monitor queue announcements (for barbers)
  useEffect(() => {
    if (!currentUser?.id || !enableMonitoring || !isInitialized) {
      return;
    }

    // Check if user is a barber
    const checkBarberStatus = async () => {
      try {
        // This would check if the current user is a barber
        // For now, we'll skip this as it requires additional context
      } catch (error) {
        console.error('Failed to check barber status:', error);
      }
    };

    checkBarberStatus();
  }, [currentUser?.id, enableMonitoring, isInitialized]);

  return {
    // Sound control methods
    playSound,
    testSound,

    // Configuration
    soundConfig,
    setSoundEnabled,
    setSoundVolume,
    setSoundTypeEnabled,
    setVibrationEnabled,

    // Status
    isInitialized,
    isMonitoring,
    currentPosition,
  };
}

/**
 * Hook for queue-wide sound notifications (for barbers/admins)
 */
export function useQueueAdminSounds(branchId?: string) {
  const [isMonitoring, setIsMonitoring] = useState(false);
  const { playSound } = useQueueSounds({ enableMonitoring: false });

  useEffect(() => {
    if (!branchId) {
      setIsMonitoring(false);
      return;
    }

    let unsubscribe: (() => void) | null = null;

    const setupMonitoring = async () => {
      try {
        // Monitor new tickets in the queue
        const queueRef = doc(db, 'branches', branchId);

        unsubscribe = onSnapshot(
          queueRef,
          (snapshot) => {
            if (!snapshot.exists()) return;

            const data = snapshot.data();
            const stats = data?.queueStats;

            // Play sound for new tickets
            // This is simplified - in production, you'd want to track
            // previous state to detect actual new tickets
            if (stats?.waiting > 0) {
              // New ticket added to queue
              playSound('notification');
            }
          },
          (error) => {
            console.error('Error monitoring queue:', error);
            setIsMonitoring(false);
          }
        );

        setIsMonitoring(true);
      } catch (error) {
        console.error('Failed to setup queue monitoring:', error);
        setIsMonitoring(false);
      }
    };

    setupMonitoring();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
      setIsMonitoring(false);
    };
  }, [branchId, playSound]);

  return {
    isMonitoring,
    playSound,
  };
}